import { Head, router, usePage } from "@inertiajs/react"
import { Ellipsis } from "lucide-react"
import { useState } from "react"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import PartnerSettingsLayout from "@/layouts/partner/settings-layout"
import PartnerLayout from "@/layouts/partner-layout"
import {
  partnerSettingsInvitationRemovalsPath,
  partnerSettingsInvitationResendsPath,
  partnerSettingsUserPath,
  partnerSettingsUsersPath,
} from "@/routes"

interface TeamMember {
  uid: string
  name: string
  email: string
  role: string
  invitation_accepted_at: string | null
  // IMP-400: invited but not yet accepted — badge + resend/revoke instead of
  // the active-member remove flow.
  pending: boolean
}

export default function TeamSettings({ users }: { users: TeamMember[] }) {
  const { auth } = usePage().props as unknown as {
    auth: { user: { uid: string } | null }
  }
  const currentUid = auth.user?.uid
  const [inviteEmail, setInviteEmail] = useState("")
  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null)

  function handleInvite() {
    if (!inviteEmail.trim()) return
    router.post(
      partnerSettingsUsersPath(),
      { partner_user: { email: inviteEmail, name: "" } },
      { onSuccess: () => setInviteEmail("") },
    )
  }

  function confirmRemove() {
    if (!removeTarget) return
    router.delete(partnerSettingsUserPath({ id: removeTarget.uid }))
    setRemoveTarget(null)
  }

  // IMP-400: pending-invitation management, mirroring the client app's
  // settings/users page (same endpoints shape, same guardrails).
  function handleResend(member: TeamMember) {
    router.post(partnerSettingsInvitationResendsPath(), { user_id: member.uid })
  }

  function handleRevoke(member: TeamMember) {
    router.post(partnerSettingsInvitationRemovalsPath(), { id: member.uid })
  }

  return (
    <PartnerLayout>
      <Head title="Settings — Team" />
      <PartnerSettingsLayout>
        <div className="flex max-w-lg flex-col gap-8">
          <div>
            <h2 className="text-sm font-semibold">Invite a member</h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Invite new members by sending them an email.
            </p>
            <div className="mt-4 flex gap-2">
              <Input
                type="email"
                placeholder="member@example.com"
                className="flex-1"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleInvite()
                  }
                }}
              />
              <Button className="rounded-md" onClick={handleInvite}>
                Invite
              </Button>
            </div>
          </div>

          <div>
            <h2 className="mb-4 text-sm font-semibold">Members</h2>
            <div>
              {users.map((member) => {
                const initials = (member.name || member.email)
                  .split(" ")
                  .map((w) => w[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()
                return (
                  <div
                    key={member.uid}
                    className="flex items-center gap-4 border-b py-4 last:border-0"
                  >
                    <div className="bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-medium">
                      {initials}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium">
                        {member.name || member.email}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-muted-foreground text-sm">
                          {member.email}
                        </div>
                        {member.pending && (
                          <Badge variant="outline">invite pending</Badge>
                        )}
                      </div>
                    </div>
                    {(member.pending || member.uid !== currentUid) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            className="text-muted-foreground hover:text-foreground p-1"
                          >
                            <Ellipsis className="size-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {member.pending ? (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleResend(member)}
                              >
                                Resend
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="text-red-600"
                                onClick={() => handleRevoke(member)}
                              >
                                Revoke
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => setRemoveTarget(member)}
                            >
                              Remove
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        </div>

        <AlertDialog
          open={!!removeTarget}
          onOpenChange={(open) => {
            if (!open) setRemoveTarget(null)
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove team member?</AlertDialogTitle>
              <AlertDialogDescription>
                This will revoke {removeTarget?.name || removeTarget?.email}
                &apos;s access to the partner portal. This action cannot be
                undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmRemove}
                className="bg-destructive hover:bg-destructive/90 text-white"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </PartnerSettingsLayout>
    </PartnerLayout>
  )
}
